import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import axios from 'axios';
import { Loader2, Radio, CheckCircle2, XCircle, MinusCircle, TrendingUp, TrendingDown, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
    open: { label: 'OPEN', color: '#3B82F6', Icon: Radio },
    tp_hit: { label: 'TP HIT', color: '#10B981', Icon: CheckCircle2 },
    sl_hit: { label: 'SL HIT', color: '#EF4444', Icon: XCircle },
    be: { label: 'BREAKEVEN', color: '#94A3B8', Icon: MinusCircle },
    cancelled: { label: 'CANCELLED', color: '#64748B', Icon: XCircle },
};

const fileUrl = (path) => (path ? `${API}/files/${path}` : null);

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.open;
    const Icon = meta.Icon;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold tracking-wide"
            style={{ backgroundColor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}40` }}
        >
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
}

export default function StudentSignals() {
    const { i18n } = useTranslation();
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('all');

    useEffect(() => {
        axios.get(`${API}/signals`, { withCredentials: true })
            .then((r) => setSignals(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = signals.filter((s) => {
        if (tab === 'all') return true;
        if (tab === 'open') return s.status === 'open';
        if (tab === 'closed') return ['tp_hit', 'sl_hit', 'be'].includes(s.status);
        return true;
    });

    const stats = {
        total: signals.length,
        open: signals.filter((s) => s.status === 'open').length,
        wins: signals.filter((s) => s.status === 'tp_hit').length,
        losses: signals.filter((s) => s.status === 'sl_hit').length,
    };
    const closedWithRR = signals.filter((s) => typeof s.rr_ratio === 'number');
    const totalR = closedWithRR.reduce((sum, s) => sum + (s.rr_ratio || 0), 0);
    const winRate = stats.wins + stats.losses > 0
        ? Math.round((stats.wins / (stats.wins + stats.losses)) * 100)
        : 0;

    const formatDate = (s) => {
        if (!s) return '—';
        const d = new Date(s);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="student-signals-title">
                        Live Trading Signals
                    </h1>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Setups posted by NIKAYOFX with entries, targets, and analysis
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="card-surface p-4">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Total</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Outfit', sans-serif" }}>{stats.total}</p>
                    </div>
                    <div className="card-surface p-4">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Open</p>
                        <p className="text-2xl font-bold" style={{ color: '#3B82F6', fontFamily: "'Outfit', sans-serif" }}>{stats.open}</p>
                    </div>
                    <div className="card-surface p-4">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Win Rate</p>
                        <p className="text-2xl font-bold" style={{ color: '#10B981', fontFamily: "'Outfit', sans-serif" }}>{winRate}%</p>
                    </div>
                    <div className="card-surface p-4">
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Total R</p>
                        <p className="text-2xl font-bold" style={{ color: totalR >= 0 ? '#10B981' : '#EF4444', fontFamily: "'Outfit', sans-serif" }}>
                            {totalR >= 0 ? '+' : ''}{totalR.toFixed(2)}R
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-2 mb-6 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    {['all', 'open', 'closed'].map((t) => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            className="px-4 py-2 text-sm font-medium capitalize transition-colors"
                            style={{
                                color: tab === t ? 'var(--primary-color)' : 'var(--text-secondary)',
                                borderBottom: tab === t ? '2px solid var(--primary-color)' : '2px solid transparent',
                                marginBottom: '-1px',
                            }}
                            data-testid={`signals-tab-${t}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                {/* Signal cards */}
                {filtered.length === 0 ? (
                    <div className="card-surface p-16 text-center">
                        <Radio className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-base" style={{ color: 'var(--text-primary)' }}>No signals to show</p>
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Check back soon — your mentor posts new setups regularly</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="signals-grid">
                        {filtered.map((s) => {
                            const DirIcon = s.direction === 'BUY' ? TrendingUp : TrendingDown;
                            const dirColor = s.direction === 'BUY' ? '#10B981' : '#EF4444';
                            return (
                                <article key={s.id} className="card-surface overflow-hidden" data-testid={`signal-card-${s.id}`}>
                                    {s.image_url && (
                                        <a href={fileUrl(s.image_url)} target="_blank" rel="noreferrer" className="block bg-black/40">
                                            <img src={fileUrl(s.image_url)} alt={`${s.market} analysis`} className="w-full h-48 object-cover hover:opacity-90 transition-opacity" />
                                        </a>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="px-2 py-0.5 rounded text-xs font-bold tracking-wider" style={{ backgroundColor: `${dirColor}18`, color: dirColor, border: `1px solid ${dirColor}40` }}>
                                                        <DirIcon className="w-3 h-3 inline mr-1" />
                                                        {s.direction}
                                                    </span>
                                                    <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                                        {s.market}
                                                    </h3>
                                                </div>
                                                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDate(s.created_at)}</p>
                                            </div>
                                            <StatusBadge status={s.status} />
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                            <div className="px-3 py-2 rounded" style={{ backgroundColor: 'var(--background-base)', border: '1px solid var(--border-subtle)' }}>
                                                <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Entry</p>
                                                <p className="text-sm font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{s.entry_price}</p>
                                            </div>
                                            <div className="px-3 py-2 rounded" style={{ backgroundColor: 'var(--background-base)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                                                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#EF4444' }}>Stop Loss</p>
                                                <p className="text-sm font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{s.stop_loss}</p>
                                            </div>
                                            <div className="px-3 py-2 rounded" style={{ backgroundColor: 'var(--background-base)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                                                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#10B981' }}>Take Profit</p>
                                                <p className="text-sm font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{s.take_profit}</p>
                                            </div>
                                        </div>

                                        {s.rr_ratio !== null && s.rr_ratio !== undefined && (
                                            <div className="flex items-center justify-between mb-3 px-3 py-2 rounded" style={{ backgroundColor: 'var(--primary-muted)' }}>
                                                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Result</span>
                                                <span className="text-base font-bold font-mono" style={{ color: s.rr_ratio > 0 ? '#10B981' : s.rr_ratio < 0 ? '#EF4444' : 'var(--text-secondary)' }}>
                                                    {s.rr_ratio > 0 ? '+' : ''}{s.rr_ratio}R
                                                </span>
                                            </div>
                                        )}

                                        {s.notes && (
                                            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                                {(s.notes_i18n && s.notes_i18n[(i18n.language || 'en').split('-')[0]]) || s.notes}
                                            </p>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}
